// ABOUTME: SPEC-82 D-13/D-14 — the collection menu's entries, highlight and clickability. The highlight is READ from the stage
// ABOUTME: machine (never held), a press is the Views button's own switch, and the only state it owns is `open`.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stage } from '../../CollectionStage/stageReducer';
import { collectionLabel, returnLabel } from '../../ReelChrome/ViewToggle/viewToggleGeometry';

const FADE_IDLE = 'idle';
const isCollectionButton = (b) => typeof b?.key === 'string' && b.key.startsWith('collection:');

/**
 * @param {object} p
 * @param {boolean} p.enabled the `collection_menu` toggle
 * @param {string} p.stage stageMachine.state.stage ('reel' | 'collection' | 'piece')
 * @param {Array|undefined} p.collections brandConfig.static_collections (tenant data; [0] is the one the Views button switches to)
 * @param {string} [p.reelRailTitle] reelProduct.rail.title — the Vesper label's source
 * @param {string} [p.stageReelTitle] stage.collection.reel_title — the label while on the stage, before the rail title is known
 * @param {string} [p.reelCollection] /api/config's tenant collection name (`waitlist.collection`) — the label at landing, before the tap
 * @param {Array} p.viewToggleButtons useReelViewToggle().buttons (its collection button IS the switch path)
 * @param {string} p.fadePhase stageFade.phase
 * @param {boolean} p.showIntro the film is on screen: only Privacy is reachable (VER-40)
 * @param {string} p.privacyLabel the existing launcher label
 * @param {Function} p.onTogglePanel App's existing panel toggle (`kind => setOverlay(...)`)
 */
export default function useCollectionMenu({
  enabled, stage, collections, reelRailTitle, stageReelTitle, reelCollection, viewToggleButtons, fadePhase, showIntro, privacyLabel, onTogglePanel,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  // SPEC-82 review L4: the popover's rows unmount on close, which drops keyboard focus to <body>; hand it back to the hamburger
  // (the first button in the menu root). Used by Escape and by a picked entry, not by a click elsewhere (that moved focus itself).
  const close = useCallback(() => {
    setOpen(false);
    rootRef.current?.querySelector('button')?.focus();
  }, []);
  const toggle = useCallback(() => setOpen((o) => !o), []);

  const switchButton = useMemo(() => (viewToggleButtons || []).find(isCollectionButton) || null, [viewToggleButtons]);
  const onReel = stage === Stage.REEL;
  const velosTitle = Array.isArray(collections) && collections.length ? collections[0]?.title : null;

  const entries = useMemo(() => {
    const list = [];
    if (!showIntro && switchButton) {
      const clickable = !switchButton.disabled && fadePhase === FADE_IDLE;
      // SPEC-82 Rev 3 (bug 3): both live titles arrive only after intro_finished (reel_product / a stage descriptor), so pre-tap the
      // entry used to vanish. The config's collection name is there at landing; the live titles still win. Omitted only with no source.
      const vesperLabel = returnLabel(reelRailTitle || stageReelTitle || reelCollection);
      if (vesperLabel) {
        list.push({ id: 'vesper', kind: 'collection', label: vesperLabel, current: onReel, disabled: onReel || !clickable, onPress: switchButton.onPress });
      }
      const velosLabel = collectionLabel(velosTitle);
      if (velosLabel) {
        list.push({ id: 'velos', kind: 'collection', label: velosLabel, current: !onReel, disabled: !onReel || !clickable, onPress: switchButton.onPress });
      }
    }
    list.push({ id: 'privacy', kind: 'privacy', label: privacyLabel, current: false, disabled: false, onPress: () => onTogglePanel('PRIVACY') });
    return list;
  }, [showIntro, switchButton, fadePhase, reelRailTitle, stageReelTitle, reelCollection, onReel, velosTitle, privacyLabel, onTogglePanel]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKeyDown = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  return { visible: !!enabled, open: !!enabled && open, toggle, close, rootRef, entries };
}
